<template>
    <div class="team-chat-app-form-wrapper px-1">
        <b-form
            v-click-outside="{ handler: 'closeEmojiPicker' }"
            class="team-chat-app-form d-flex justify-content-between flex-nowrap align-items-center bg-white px-2 shadow rounded"
            inline
            @drop.prevent="onDrop"
        >
            <b-form-file v-show="false" id="fileUploads" v-model="fileUploads" multiple @input="uploadAssets" />

            <active-team-chat-asset-preview />
            <b-button
                id="compose-option-attachment"
                class="p-0 rounded-circle"
                type="button"
                variant="primary"
                @click="openFilePrompt"
            >
                <font-awesome-icon icon="plus" size="xs" />
            </b-button>

            <b-input-group class="send-message-form-group flex-grow-1 mx-1">
                <b-form-textarea
                    ref="writeMessage"
                    v-model="chatEntryInput.message"
                    :disabled="false"
                    :placeholder="$t('team-chats.write-message-placeholder')"
                    autocomplete="off"
                    class="bg-white border-0"
                    name="teamChatsWriteMessage"
                    no-resize
                    rows="2"
                    @input="setUserTyping"
                    @keydown.enter.exact.prevent="enterToSend ? sendMessage() : false"
                    @keydown.enter.shift.exact="enterToSend ? false : sendMessage()"
                />
            </b-input-group>

            <div v-show="shallShowEmojiPicker" class="team-chat-emoji-picker">
                <VEmojiPicker @select="selectEmoji($event, 'teamChatsWriteMessage')" />
            </div>

            <b-button type="button" variant="flat-secondary" @click="toggleEmojiPicker">
                <font-awesome-icon :icon="['fa', 'laugh']" class="cursor-pointer" size="xl" />
            </b-button>
            <b-button
                id="send-message"
                :disabled="isUploadingFilesPending || isInputEmpty"
                class="rounded"
                type="button"
                variant="primary"
                @click="sendMessage"
            >
                <b-spinner v-if="isUploadingFilesPending" small></b-spinner>
                <font-awesome-icon v-else :icon="['fa', 'paper-plane-top']" class="cursor-pointer" />
            </b-button>
        </b-form>
    </div>
</template>

<script>
import {
    BButton,
    BDropdown,
    BDropdownItemButton,
    BForm,
    BFormFile,
    BFormInput,
    BFormTextarea,
    BInputGroup,
    BSpinner,
} from "bootstrap-vue";
import { useFocus } from "@vueuse/core";
import { ref, watchEffect, onMounted, onUnmounted } from "@vue/composition-api";
import { VEmojiPicker } from "v-emoji-picker";
import useActiveTeamChat from "../useActiveTeamChat";
import useAppConfig from "@/composables/useAppConfig";
import { uploadSingle } from "@/utils/assets";
import ActiveTeamChatAssetPreview from "./activeTeamChatAssetPreview.vue";
import { useToast } from "vue-toastification/composition";
import { addDataToInput } from "@/utils/input.ts";
import { useWebsockets } from "@/libs/websockets";

export default {
    components: {
        BButton,
        BDropdown,
        BDropdownItemButton,
        BForm,
        BFormFile,
        BFormInput,
        BFormTextarea,
        BInputGroup,
        BSpinner,

        VEmojiPicker,
        ActiveTeamChatAssetPreview,
    },
    props: {
        activeChat: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            shallShowEmojiPicker: false,
        };
    },
    computed: {
        isUploadingFilesPending() {
            return this.chatEntryInput.attachments.some((attachment) => Boolean(attachment.isUploadingFilePending));
        },
        isInputEmpty() {
            return this.chatEntryInput.message === "" && this.chatEntryInput.attachments.length === 0;
        },
    },
    methods: {
        onDrop(e) {
            const files = e.dataTransfer.files;
            if (files.length) {
                this.fileUploads.push(...files);

                this.uploadAssets();
            }
        },
        selectEmoji(emoji, name) {
            this.chatEntryInput.message = addDataToInput(name, this.chatEntryInput.message || "", emoji.data);
            this.shallShowEmojiPicker = false;
        },
        toggleEmojiPicker(e) {
            this.shallShowEmojiPicker = !this.shallShowEmojiPicker;
            this.$refs.writeMessage.focus();
            if (!this.shallShowEmojiPicker) {
                e.currentTarget.blur();
            }
        },
        closeEmojiPicker() {
            this.shallShowEmojiPicker = false;
        },
        openFilePrompt(e) {
            document.getElementById("fileUploads").click();
            e.currentTarget.blur();
        },
        async uploadAssets() {
            const uniqueFiles = this.fileUploads.filter(
                (file) => !this.chatEntryInput.attachments.some((attachment) => attachment.originalName === file.name)
            );
            const tmpAssets = uniqueFiles.map((file) => ({
                name: file.name,
                isUploadingFilePending: true,
            }));

            this.chatEntryInput.attachments = this.chatEntryInput.attachments.concat(tmpAssets);
            await Promise.all(
                uniqueFiles.map((asset) =>
                    uploadSingle(asset)
                        .then((response) => {
                            const index = this.chatEntryInput.attachments.findIndex(
                                (attachment) => attachment.name === response.originalName
                            );
                            if (index === -1) {
                                return;
                            }
                            //NOTE: we are using splice instead of '[index] =' as a workaround to make ref work properly
                            this.chatEntryInput.attachments.splice(index, 1, response);
                        })
                        .catch(() => {
                            const index = this.chatEntryInput.attachments.findIndex(
                                (attachment) => attachment.name === asset.name
                            );
                            this.chatEntryInput.attachments.splice(index, 1);
                            this.toast.error(this.$i18n.t("upload.failed"));
                        })
                )
            ).catch(() => {
                this.toast.error(this.$i18n.t("upload.failed"));
            });
        },
    },
    setup(props) {
        const { sendWebsocketsMessage } = useWebsockets();
        const { chatEntryInput, sendMessage, isSendPending, fileUploads, setUserTyping } =
            useActiveTeamChat(sendWebsocketsMessage);
        const { enterToSend } = useAppConfig();
        const toast = useToast();

        const writeMessage = ref();

        // set focus to input field when the active chat has been changed.
        watchEffect(
            () => props.activeChat,
            () => useFocus(writeMessage, { initialValue: true })
        );

        function preventDefaults(e) {
            e.preventDefault();
        }

        const events = ["dragenter", "dragover", "dragleave", "drop"];

        onMounted(() => {
            events.forEach((eventName) => {
                document.body.addEventListener(eventName, preventDefaults);
            });
        });

        onUnmounted(() => {
            events.forEach((eventName) => {
                document.body.removeEventListener(eventName, preventDefaults);
            });
        });

        return {
            isSendPending,
            writeMessage,
            sendMessage,
            enterToSend,
            fileUploads,
            chatEntryInput,
            setUserTyping,
            toast,
        };
    },
};
</script>

<style lang="scss">
@import "~@/assets/scss/vEmojiPicker.scss";
</style>
